import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  TextField,
} from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "@ckeditor/ckeditor5-build-classic";
import { useState } from "react";
import { toast } from "react-toastify";
import { postQuestionaireQuestionsService } from "../../../services/questionaire.service";
import { configToast, questionTypes, statusTypes } from "../../../constants";

const CreateQuestionDialog = ({ questionaireId, handleClose, order }: any) => {
  const [questionType, setQuestionType] = useState<any>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const { register, handleSubmit, control, formState, setValue } = useForm({
    mode: "all",
    defaultValues: {
      questionaire_id: questionaireId,
      question_type: "",
      question: "",
      question_order: order,
      answer_option: {
        min: null,
        max: null,
      },
      is_active: null,
      remark: "",
      answer_options: [
        {
          answer_order: 1,
          answer: "",
          next_question: null,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answer_options",
  });

  const { isValid } = formState;

  const onAddAnswer = () => {
    append({
      answer_order: fields.length + 1,
      answer: "",
      next_question: null,
    });
  };

  const handleQuestionTypeChange = (event: any) => {
    setValue("answer_options", [
      {
        answer_order: 1,
        answer: "",
        next_question: null,
      },
    ]);
    setValue("answer_option", {
      min: null,
      max: null,
    });
    setQuestionType(event.target.value);
  };

  const handleEditorQuestionChange = (event: any, editor: any) => {
    const data = editor.getData();
    setValue("question", data);
  };

  const getContentAnswerOptions = (type: any) => {
    switch (type) {
      case 3:
        return (
          <Box display="flex" justifyContent="space-between" gap="16px">
            <FormControl fullWidth>
              <Controller
                key="answer-option-min"
                name="answer_option.min"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    type="number"
                    label="ค่าต่ำสุด"
                    value={value}
                    onChange={onChange}
                    placeholder="กรอกค่าต่ำสุด"
                    size="medium"
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth>
              <Controller
                key="answer-option-max"
                name="answer_option.max"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    type="number"
                    label="ค่าสูงสุด"
                    value={value}
                    onChange={onChange}
                    placeholder="กรอกค่าสูงสุด"
                    size="medium"
                  />
                )}
              />
            </FormControl>
          </Box>
        );

      case 4:
        return (
          <Box display="flex" flexDirection="column" gap="8px">
            <Typography typography="body1" color="cGrey.main">
              ตัวเลือก
            </Typography>

            {fields.map((question: any, index) => (
              <Box display="flex" gap="8px">
                <FormControl key={question.id} fullWidth>
                  <TextField
                    type="text"
                    label={`ตัวเลือกที่ ${index + 1}`}
                    placeholder="กรอกตัวเลือก"
                    size="small"
                    {...register(`answer_options.${index}.answer`, {
                      required: true,
                    })}
                  />
                </FormControl>

                <FormControl key={question.id}>
                  <TextField
                    type="number"
                    label={`คำถามถัดไป`}
                    placeholder="กรอกคำถามถัดไป"
                    size="small"
                    {...register(`answer_options.${index}.next_question`)}
                  />
                </FormControl>

                <Box
                  display="flex"
                  alignItems="center"
                  component="a"
                  sx={{ cursor: "pointer" }}
                  onClick={() => remove(index)}
                >
                  <RemoveCircleOutlineIcon sx={{ color: "red.main" }} />
                </Box>
              </Box>
            ))}

            <Box display="flex" justifyContent="end">
              <Typography
                typography="body1"
                color="primary.main"
                sx={{ cursor: "pointer" }}
                onClick={onAddAnswer}
              >
                เพิ่มตัวเลือก
              </Typography>
            </Box>
          </Box>
        );

      default:
        break;
    }
  };

  const onSubmitHandler: SubmitHandler<any> = async (values) => {
    try {
      setSaving(true);

      let answer_options = null;

      if (values.question_type === 3) {
        answer_options = values.answer_option;
      } else if (values.question_type === 4) {
        answer_options = values.answer_options;
      }

      await postQuestionaireQuestionsService({
        questionaire_id: values.questionaire_id,
        question_order: values.question_order,
        question_type: values.question_type,
        question: values.question,
        remark: values.remark,
        answer_options,
        is_active: !values.is_active ? true : false,
      });

      handleClose(true);
      toast.success("สร้างคำถามสำเร็จ", configToast);
    } catch (error: any) {
      const resMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      toast.error(resMessage || "สร้างคำถามไม่สำเร็จ", configToast);
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmitHandler)}>
      <DialogTitle>
        <Typography variant="h5">สร้างคำถาม</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap="16px">
          <FormControl fullWidth>
            <CKEditor
              editor={Editor}
              config={{ placeholder: "คำถาม" }}
              onChange={handleEditorQuestionChange}
            />
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="question-type">ประเภทคำถาม</InputLabel>
            <Select
              labelId="question-type"
              id="question-type"
              placeholder="เลือกประเภทคำถาม"
              label="ประเภทคำถาม"
              {...register("question_type", { required: true })}
              size="medium"
              onChange={handleQuestionTypeChange}
            >
              {questionTypes.map((type: any, index: number) => (
                <MenuItem key={index} value={index}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {getContentAnswerOptions(questionType)}

          <FormControl fullWidth>
            <InputLabel id="status-label">สถานะ</InputLabel>
            <Select
              labelId="status-label"
              id="status-label"
              placeholder="เลือกสถานะ"
              label="สถานะ"
              {...register("is_active", { required: true })}
              size="medium"
            >
              {statusTypes.map((status: string, index: number) => (
                <MenuItem key={index} value={index}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <TextField
              type="text"
              label="Remark"
              placeholder="กรอกRemark"
              {...register("remark")}
              size="medium"
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          color="red"
          variant="outlined"
          type="button"
          onClick={handleClose}
        >
          <Typography variant="subtitle2">ยกเลิก</Typography>
        </Button>

        <Button
          color="green"
          variant="outlined"
          type="submit"
          disabled={!isValid || saving}
        >
          <Typography variant="subtitle2">ยืนยัน</Typography>
        </Button>
      </DialogActions>
    </Box>
  );
};

export default CreateQuestionDialog;
